<template>
  <b-row class="mb-3 align-items-center">
    <b-col sm="2">Filter by coupon:</b-col>
    <b-col sm="4">
      <b-form-input v-model="couponQueryStr" placeholder="Enter coupon code" @keydown.enter="resetAndSearch()" />
    </b-col>
    <b-col sm="4" v-if="couponQueryStr">
      <b-button @click="resetAndSearch()" variant="outline-info" class="mr-1">Search</b-button>
      <b-button @click="clearSearch()" variant="outline-warning">Clear</b-button>
    </b-col>
  </b-row>
  <b-row class="mb-4 align-items-center">
    <b-col sm="2">Status:</b-col>
    <b-col sm="10">
      <b-form-checkbox-group id="checkbox-group-status" v-model="statuses" :options="statusesOptions" name="status" />
    </b-col>
  </b-row>
  <b-row>
    <b-col class="mb-5">
      <transition name="fade" mode="out-in">
        <div v-if="loading" class="d-flex justify-content-center mt-4 mb-4">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
        </div>
        <div v-else>
          <div class="coupons-table-wrap">
            <b-table responsive hover :fields="fields" :items="coupons">
              <template #cell(coupon)="data">
                <div
                  @click="copyToCb($refs['coupon' + data.item.id], data.item.coupon)"
                  :ref="`coupon${data.item.id}`"
                  :title="data.item.coupon"
                  class="ellipsis"
                >
                  {{ data.item.coupon }}
                </div>
              </template>
              <template #cell(amount)="data">{{ data.item.amount }} {{ data.item.currency }}</template>
              <template #cell(status)="data">
                <CouponStatusSelect :id="data.item.id" :status="data.item.status" :project="project" />
              </template>
              <template #cell(user_id)="data">
                <b-button
                  v-if="data.item.user_fbuid"
                  @click="redirectToUser(data.item.user_fbuid)"
                  class="noDecoration"
                  variant="link"
                >
                  {{ data.item.user_id }}
                </b-button>
                <span v-else>{{ data.item.user_id }}</span>
              </template>
              <template #cell(customer_id)="data">
                <div
                  @click="copyToCb($refs['customer' + data.item.id], data.item.customer_id)"
                  :ref="`customer${data.item.id}`"
                  class="cursorPointer"
                >
                  {{ data.item.customer_id }}
                </div>
              </template>
              <template #cell(updated_at)="data">
                {{ data.item.updated_at ? format(new Date(data.item.updated_at), 'yyyy-MM-dd HH:mm:ss') : '' }}
              </template>
            </b-table>
          </div>
          <Paginator
            @next="showNextPage"
            @prev="showPrevPage"
            @selectPage="selectPage"
            :pagesCount="this.pagesCount"
            :currentPage="this.currentPage"
            :limit="this.limit"
            :itemsCount="this.itemsCount"
            class="pagination mt-4 mb-4"
          />
        </div>
      </transition>
    </b-col>
  </b-row>
</template>

<script>
import { format } from 'date-fns'
import { useActAdminApi } from '@/use/actAdminApi'
import { useSearch } from '@/use/search'
import { useAuth } from '@/use/auth'
import { useCopy } from '@/use/copy'
import Paginator from '@/components/Paginator.vue'
import CouponStatusSelect from '@/components/Coupons/CouponStatusSelect.vue'

export default {
  components: { Paginator, CouponStatusSelect },
  props: {
    project: {
      type: String,
      default: 'platfone'
    }
  },
  data() {
    const commonStart = [
      { key: 'id', label: 'ID', tdClass: 'align-middle' },
      { key: 'coupon', label: 'Coupon', tdClass: 'align-middle coupon-col' },
      { key: 'amount', label: 'Amount', tdClass: 'align-middle' }
    ]

    const commonEnd = [
      { key: 'updated_at', label: 'Updated', tdClass: 'align-middle' },
      { key: 'status', label: 'Status', tdClass: 'align-middle' }
    ]

    const projectField =
      this.project === 'platfone'
        ? { key: 'user_id', label: 'User ID', tdClass: 'align-middle' }
        : { key: 'customer_id', label: 'Customer ID', tdClass: 'align-middle customer-col' }

    return {
      format,
      loading: true,
      coupons: [],
      fields: [...commonStart, projectField, ...commonEnd],
      currentPage: 1,
      limit: 50,
      pagesCount: 1,
      itemsCount: null,
      statuses: [],
      couponQueryStr: '',
      statusesOptions: [
        { text: 'Active', value: 1 },
        { text: 'Used', value: 2 },
        { text: 'Disabled', value: 3 },
        { text: 'Refunded', value: 4 }
      ]
    }
  },
  setup() {
    const { initApiInst, actPaymentCouponsApi } = useActAdminApi()
    const { isSuperAdmin } = useAuth()
    const { copyToCb } = useCopy()
    const { getSearchData } = useSearch()
    return { initApiInst, actPaymentCouponsApi, isSuperAdmin, copyToCb, getSearchData }
  },
  mounted() {
    this.getCoupons()
  },
  watch: {
    statuses() {
      this.currentPage = 1
      this.getCoupons()
    },
    project() {
      this.coupons = []
      this.currentPage = 1
      this.couponQueryStr = ''
      this.statuses = []
      this.getCoupons()
    }
  },
  methods: {
    async getCoupons() {
      this.loading = true
      const reqData = {
        page: this.currentPage,
        project: this.project
      }
      if (this.couponQueryStr) reqData.coupon = this.couponQueryStr
      if (this.statuses.length > 0) reqData.status = this.statuses
      try {
        await this.initApiInst()
        const resp = await this.actPaymentCouponsApi.getPaymentCoupons(reqData)
        if (resp && resp.coupons) {
          this.coupons = resp.coupons
          this.currentPage = resp.page
          this.limit = resp.limit
          this.pagesCount = resp.pages
          this.itemsCount = resp.total
        }
      } catch (e) {
        this.coupons = []
      } finally {
        this.loading = false
      }
    },
    async getByPage(page) {
      this.currentPage = page
      await this.getCoupons()
    },
    async showNextPage() {
      await this.getByPage(this.currentPage + 1)
    },
    async showPrevPage() {
      await this.getByPage(this.currentPage - 1)
    },
    async selectPage(page) {
      await this.getByPage(page)
    },
    redirectToUser(fbUid) {
      this.getSearchData(fbUid)
      this.$router.push({ path: '/' })
    },
    resetAndSearch() {
      this.currentPage = 1
      this.getCoupons()
    },
    clearSearch() {
      this.couponQueryStr = ''
      this.currentPage = 1
      this.getCoupons()
    }
  }
}
</script>

<style lang="scss">
.coupon-col {
  min-width: 280px;
}
.customer-col {
  min-width: 300px;
}
.coupons-table-wrap .table-responsive {
  overflow: visible;
  min-height: 250px;
}
</style>

<style lang="scss" scoped>
@import '../../assets/animations/fade-data.scss';

.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 280px;
  display: block;
  cursor: pointer;
}
.noDecoration {
  text-decoration: none;
}
.cursorPointer {
  cursor: pointer;
}
.mr-1 {
  margin-right: 8px;
}
</style>
